/* Grafici, in SVG scritto a mano.
 *
 * Nessuna libreria: sono tre forme, e una libreria di grafici porta con sé un
 * proprio sistema di colori che entrerebbe in conflitto con la regola su verde
 * e rosso. Qui i colori vengono dai token e da nessun'altra parte.
 *
 * "Rendimento contro rischio" resta il grafico centrale: è l'unico che mostra
 * le due grandezze insieme senza che se ne possa leggere una sola.
 */

import { useId, type ReactNode } from 'react';
import { num, pct } from '../lib/formato';
import { aPariRischio, DRAWDOWN_OBIETTIVO } from '../lib/metriche';
import { ContenitoreDati } from './Stati';
import type { StatoDati } from '../api/usaRichiesta';

interface PropsComuni {
  stato?: StatoDati;
  causaVuoto?: string;
  messaggioErrore?: string;
  onRiprova?: () => void;
}

function GuscioGrafico({
  stato = 'pieno',
  causaVuoto = 'Non ci sono punti da disegnare su questo periodo.',
  messaggioErrore,
  onRiprova,
  altezza,
  children,
}: PropsComuni & { altezza: number; children: ReactNode }) {
  return (
    <ContenitoreDati
      stato={stato}
      causaVuoto={causaVuoto}
      messaggioErrore={messaggioErrore}
      onRiprova={onRiprova}
      scheletro={<div className="sb-scheletro" style={{ height: altezza, borderRadius: 'var(--sb-r-3)' }} />}
    >
      {children}
    </ContenitoreDati>
  );
}

/* ==========================================================================
 * EQUITY
 * ======================================================================== */

export interface PuntoEquity {
  timestamp: number;
  value: number;
}

/**
 * La scala logaritmica non è un vezzo: su un +10.486% la scala lineare
 * schiaccia i primi anni contro l'asse e fa sembrare che la strategia non
 * facesse nulla fino all'ultimo tratto.
 */
export function GraficoEquity({
  punti,
  altezza = 190,
  logaritmica = true,
  ...comuni
}: PropsComuni & { punti: PuntoEquity[]; altezza?: number; logaritmica?: boolean }) {
  const idSfumatura = useId().replace(/:/g, '');
  const L = 520;
  const pad = 8;

  const stato = comuni.stato ?? (punti.length === 0 ? 'vuoto' : 'pieno');

  if (stato !== 'pieno') {
    return (
      <GuscioGrafico {...comuni} stato={stato} altezza={altezza}>
        {null}
      </GuscioGrafico>
    );
  }

  /* Con la scala logaritmica un valore <= 0 non ha logaritmo: si sostituisce
     con un minimo positivo invece di far collassare l'intero grafico. */
  const valori = punti.map((p) => (logaritmica ? Math.log10(Math.max(p.value, 0.0001)) : p.value));
  const min = Math.min(...valori);
  const max = Math.max(...valori);
  const ampiezza = max - min || 1;

  const X = (i: number) => (i / Math.max(1, punti.length - 1)) * L;
  const Y = (v: number) => pad + (1 - (v - min) / ampiezza) * (altezza - 2 * pad);

  const linea = valori
    .map((v, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`)
    .join(' ');

  return (
    <svg
      viewBox={`0 0 ${L} ${altezza}`}
      width="100%"
      height={altezza}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
      role="img"
      aria-label="Andamento del capitale nel tempo"
    >
      <defs>
        <linearGradient id={idSfumatura} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--sb-guadagno)" stopOpacity="0.26" />
          <stop offset="100%" stopColor="var(--sb-guadagno)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((g) => (
        <line
          key={g}
          x1={0}
          x2={L}
          y1={pad + g * (altezza - 2 * pad)}
          y2={pad + g * (altezza - 2 * pad)}
          stroke="var(--sb-bordo-tenue)"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      <path d={`${linea} L ${L} ${altezza} L 0 ${altezza} Z`} fill={`url(#${idSfumatura})`} />
      <path
        d={linea}
        fill="none"
        stroke="var(--sb-guadagno)"
        strokeWidth={1.9}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/* ==========================================================================
 * RENDIMENTO CONTRO RISCHIO
 * ======================================================================== */

export interface PuntoRischio {
  id: number;
  profitto: number;
  drawdown: number;
  etichetta?: string;
}

/** In alto a sinistra = migliore. I punti evidenziati sono i primi per valore
 *  a pari rischio, non per profitto nominale: è quella la classifica vera. */
export function GraficoRendimentoRischio({
  punti,
  quantiInEvidenza = 5,
  altezza = 300,
  ...comuni
}: PropsComuni & { punti: PuntoRischio[]; quantiInEvidenza?: number; altezza?: number }) {
  const L = 520;
  const padSinistra = 48;
  const padBasso = 40;

  const stato = comuni.stato ?? (punti.length === 0 ? 'vuoto' : 'pieno');
  if (stato !== 'pieno') {
    return (
      <GuscioGrafico {...comuni} stato={stato} altezza={altezza}>
        {null}
      </GuscioGrafico>
    );
  }

  const conPunteggio = punti.map((p) => ({
    ...p,
    punteggio: aPariRischio(p.profitto, p.drawdown, DRAWDOWN_OBIETTIVO),
  }));
  const migliori = new Set(
    [...conPunteggio]
      .sort((a, b) => b.punteggio - a.punteggio)
      .slice(0, quantiInEvidenza)
      .map((p) => p.id),
  );

  const ddMax = Math.max(56, ...punti.map((p) => Math.abs(p.drawdown))) * 1.02;
  const retMax = Math.max(...punti.map((p) => p.profitto)) * 1.06 || 1;

  const X = (dd: number) => padSinistra + (Math.abs(dd) / ddMax) * (L - padSinistra - 14);
  const Y = (ret: number) => altezza - padBasso - (ret / retMax) * (altezza - padBasso - 14);

  const tacche = [0, 0.25, 0.5, 0.75, 1];

  return (
    <svg
      viewBox={`0 0 ${L} ${altezza}`}
      width="100%"
      height={altezza}
      preserveAspectRatio="xMidYMid meet"
      style={{ display: 'block' }}
      role="img"
      aria-label="Rendimento contro rischio: in alto a sinistra è migliore"
    >
      {tacche.map((g, i) => {
        const y = altezza - padBasso - g * (altezza - padBasso - 14);
        return (
          <g key={i}>
            <line x1={padSinistra} y1={y} x2={L - 8} y2={y} stroke="var(--sb-bordo-tenue)" strokeWidth={1} />
            <text
              x={padSinistra - 7}
              y={y + 3.5}
              fill="var(--sb-testo-tenue)"
              fontSize={9}
              fontFamily="JetBrains Mono"
              textAnchor="end"
            >
              {num(Math.round((retMax * g) / 100) * 100)}%
            </text>
          </g>
        );
      })}

      {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
        <text
          key={i}
          x={X(ddMax * f)}
          y={altezza - padBasso + 15}
          fill="var(--sb-testo-tenue)"
          fontSize={9}
          fontFamily="JetBrains Mono"
          textAnchor="middle"
        >
          −{Math.round(ddMax * f)}%
        </text>
      ))}

      <line x1={padSinistra} y1={altezza - padBasso} x2={L - 8} y2={altezza - padBasso} stroke="var(--sb-bordo-forte)" />
      <line x1={padSinistra} y1={10} x2={padSinistra} y2={altezza - padBasso} stroke="var(--sb-bordo-forte)" />

      <text
        x={(L + padSinistra) / 2}
        y={altezza - 6}
        fill="var(--sb-testo-tenue)"
        fontSize={10}
        fontFamily="JetBrains Mono"
        textAnchor="middle"
      >
        drawdown massimo
      </text>
      <text
        x={12}
        y={(altezza - padBasso) / 2}
        fill="var(--sb-testo-tenue)"
        fontSize={10}
        fontFamily="JetBrains Mono"
        textAnchor="middle"
        transform={`rotate(-90 12 ${(altezza - padBasso) / 2})`}
      >
        profitto
      </text>

      {conPunteggio.map((p) => {
        const top = migliori.has(p.id);
        return (
          <circle
            key={p.id}
            cx={X(p.drawdown)}
            cy={Y(p.profitto)}
            r={top ? 6.5 : 4.5}
            /* L'evidenza è blu, non verde: qui non si dice "guadagna", si dice
               "questo è quello selezionato". */
            fill={top ? 'var(--sb-accento)' : '#333f4f'}
            stroke={top ? 'var(--sb-accento-chiaro)' : 'none'}
            strokeWidth={top ? 1.8 : 0}
            opacity={top ? 1 : 0.72}
          >
            <title>
              {p.etichetta ?? `#${p.id}`} · {pct(p.profitto)} / −{num(Math.abs(p.drawdown), 1)}%
            </title>
          </circle>
        );
      })}
    </svg>
  );
}

/* ==========================================================================
 * CURVA DI PROFONDITÀ
 * ======================================================================== */

export interface PuntoProfonditaGrafico {
  amountUsd: number;
  impactPct: number | null;
}

/** Quanto capitale si può muovere prima che lo slippage mangi il vantaggio.
 *  L'unica soglia che conta è dove la curva sfonda: si disegna esplicitamente. */
export function GraficoProfondita({
  punti,
  sogliaPct,
  altezza = 190,
  ...comuni
}: PropsComuni & { punti: PuntoProfonditaGrafico[]; sogliaPct: number; altezza?: number }) {
  const L = 520;
  const pad = 8;

  const utilizzabili = punti.filter((p) => p.impactPct != null) as {
    amountUsd: number;
    impactPct: number;
  }[];

  const stato = comuni.stato ?? (utilizzabili.length < 2 ? 'vuoto' : 'pieno');
  if (stato !== 'pieno') {
    return (
      <GuscioGrafico
        {...comuni}
        stato={stato}
        altezza={altezza}
        causaVuoto={comuni.causaVuoto ?? 'Nessun preventivo utilizzabile: i provider non hanno risposto.'}
      >
        {null}
      </GuscioGrafico>
    );
  }

  const maxImporto = Math.max(...utilizzabili.map((p) => p.amountUsd));
  const maxImpatto = Math.max(sogliaPct * 1.4, ...utilizzabili.map((p) => p.impactPct));

  const X = (v: number) => (v / maxImporto) * L;
  const Y = (v: number) => pad + (1 - v / maxImpatto) * (altezza - 2 * pad);

  const linea = utilizzabili
    .map((p, i) => `${i ? 'L' : 'M'}${X(p.amountUsd).toFixed(1)} ${Y(p.impactPct).toFixed(1)}`)
    .join(' ');

  return (
    <svg
      viewBox={`0 0 ${L} ${altezza}`}
      width="100%"
      height={altezza}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
      role="img"
      aria-label="Impatto sul prezzo al crescere dell'importo"
    >
      <line
        x1={0}
        x2={L}
        y1={Y(sogliaPct)}
        y2={Y(sogliaPct)}
        stroke="var(--sb-cautela)"
        strokeWidth={1}
        strokeDasharray="4 4"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={`${linea} L ${L} ${altezza} L 0 ${altezza} Z`}
        fill="var(--sb-perdita-velo)"
      />
      <path
        d={linea}
        fill="none"
        stroke="var(--sb-perdita)"
        strokeWidth={1.8}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
